// Ruta: packages/auth/src/auth-store.ts
// Zustand store for auth state — access token lives ONLY in memory (no persist)

import { create } from 'zustand'
import type { AuthUser, AuthStatus } from './types.js'

/** Decode JWT payload without verification (for UI display only) */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payloadB64] = token.split('.')
    if (!payloadB64) return null
    const padded = payloadB64.padEnd(payloadB64.length + ((4 - (payloadB64.length % 4)) % 4), '=')
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return null
  }
}

interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  status: AuthStatus
  isAuthenticated: boolean
  isLoading: boolean
  setTokens: (accessToken: string, refreshToken: string, user: AuthUser) => void
  setAccessToken: (token: string) => void
  clearAuth: () => void
  setStatus: (status: AuthStatus) => void
  hasPermission: (permission: string) => boolean
  hasRole: (role: string) => boolean
  hasStoreAccess: (storeId: number) => boolean
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  status: 'idle' as AuthStatus,
  isAuthenticated: false,
  isLoading: true,

  setTokens: (accessToken: string, _refreshToken: string, user: AuthUser) => {
    set({ accessToken, user, status: 'authenticated', isAuthenticated: true, isLoading: false })
  },

  setAccessToken: (token: string) => {
    set({ accessToken: token })
  },

  clearAuth: () => {
    set({ user: null, accessToken: null, status: 'unauthenticated', isAuthenticated: false, isLoading: false })
  },

  setStatus: (status: AuthStatus) => {
    set({
      status,
      isAuthenticated: status === 'authenticated',
      isLoading: status === 'loading' || status === 'idle',
    })
  },

  hasPermission: (permission: string) => {
    const { accessToken } = get()
    if (!accessToken) return false
    const payload = decodeJwtPayload(accessToken)
    if (!payload) return false
    const permissions = (payload['permissions'] as string[]) ?? []
    return permissions.includes(permission)
  },

  hasRole: (role: string) => {
    const { user } = get()
    if (!user) return false
    return user.role === role
  },

  hasStoreAccess: (storeId: number) => {
    const { accessToken, user } = get()
    if (!user) return false
    if (user.role === 'superadmin') return true
    if (!accessToken) return false
    const payload = decodeJwtPayload(accessToken)
    if (!payload) return false
    const stores = (payload['stores'] as number[]) ?? []
    return stores.includes(storeId)
  },
}))
